import React, { Fragment, ChangeEvent } from 'react';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { ScaleLoader } from 'react-spinners';
import { Box, Switch, Card, TextField, MenuItem } from '@material-ui/core';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { IReferentialService } from '../../../services/Interfaces/IReferentialService';
import { ITranslation, IReferential, IReferentialConfig } from '../../../interfaces/IReferential';
import Autocomplete, { RenderInputParams } from '@material-ui/lab/Autocomplete';
import MaterialTable, { Icons, EditComponentProps, Column } from 'material-table';
import Referentials from '../../../helpers/Referentials.json';
import { withSnackbar, ProviderContext } from 'notistack';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { forwardRef } from 'react';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import { Label } from 'reactstrap';
import moment from 'moment';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import { Language } from '../../../interfaces/Organization';
import * as R from 'ramda';
import { ReferentialCode } from '../../../helpers/Constants';
import CityAppReferentials from './CityAppReferentials';
import LeaseProviderAppReferentials from './LeaseProviderAppReferentials';
import ListValuesAppReferentials from './ListValuesAppReferentials';
import TiresDiameterAppReferentials from './TiresDiameterAppReferentials';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddCircleTwoToneIcon {...props} ref={ref} color="secondary" />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
} as Icons;

type ReferentialProps = { appState: AppState } & ProviderContext;

interface IReferentialState {
  dependentReferencials: IReferential[];
  referentials: IReferential[];
  filteredReferentials: IReferential[];
  isLoading: boolean;
  selectedReferential: IReferentialConfig | null;
  selectedDependentReferential: IReferential | null;
  dependentReferentialConfig: IReferentialConfig | null;
  languages: Language[];
  organizationTypeReferential: boolean;
  fuelTypeReferential: boolean;
  vehicleStatusReferential: boolean;
  featureReferential: boolean;
  caseStatusReferential: boolean;
  tiresDiameterReferential: boolean;
}

class AppReferentials extends React.PureComponent<ReferentialProps, IReferentialState> {
  private translatorService!: ITranslatorService;
  private appReferentialService!: IReferentialService;

  static contextType = ApplicationContext;

  state = {
    referentials: [],
    isLoading: false,
    selectedReferential: null,
    languages: [] as Language[],
    dependentReferencials: [],
    dependentReferentialConfig: null,
    selectedDependentReferential: null,
    filteredReferentials: [],
    organizationTypeReferential: false,
    fuelTypeReferential: false,
    vehicleStatusReferential: false,
    featureReferential: false,
    caseStatusReferential: false,
    tiresDiameterReferential: false
  } as IReferentialState;

  componentDidMount = () => {
    this.setState({
      languages: this.activeLanguages()
    });
  };

  onTagsChange = (event: React.ChangeEvent<any>, value: IReferentialConfig | null) => {
    if (value?.code === ReferentialCode.City) {
      this.setState({
        selectedReferential: value!,
        dependentReferencials: [],
        dependentReferentialConfig: null,
        selectedDependentReferential: null
      });
      return;
    }

    if (value?.code === ReferentialCode.TiresDiameter) {
      this.setState({
        selectedReferential: value!,
        dependentReferencials: [],
        dependentReferentialConfig: null,
        selectedDependentReferential: null
      });
      return;
    }

    if (value?.code === ReferentialCode.LeaseProvider) {
      this.setState({
        selectedReferential: value!,
        dependentReferencials: [],
        dependentReferentialConfig: null,
        selectedDependentReferential: null
      });
      return;
    }

    if (value?.code === ReferentialCode.ListValues) {
      this.setState({
        selectedReferential: value!,
        dependentReferencials: [],
        dependentReferentialConfig: null,
        selectedDependentReferential: null
      });
      return;
    }

    this.setState(
      {
        isLoading: true,
        dependentReferencials: [],
        dependentReferentialConfig: null,
        selectedDependentReferential: null,
        organizationTypeReferential:
          value?.code === ReferentialCode.OrganizationType ? true : false,
        fuelTypeReferential: value?.code === ReferentialCode.FuelType ? true : false,
        vehicleStatusReferential: value?.code === ReferentialCode.VehicleStatus ? true : false,
        featureReferential: value?.code === ReferentialCode.Feature ? true : false,
        caseStatusReferential: value?.code === ReferentialCode.CaseStatus ? true : false,
        tiresDiameterReferential: value?.code === ReferentialCode.TiresDiameter ? true : false
      },
      () => {
        this.loadReferentials(value);
      }
    );
  };

  handleDependentReferencial = (newValue: IReferential | null) => {
    if (R.isNil(newValue)) {
      this.setState({
        ...this.state,
        selectedDependentReferential: newValue,
        filteredReferentials: this.state.referentials
      });
      return;
    }

    const referentials = this.state.referentials.filter((ref) => ref.dependencyId === newValue.id);
    this.setState({
      ...this.state,
      selectedDependentReferential: newValue,
      filteredReferentials: referentials
    });
  };

  loadDependentReferentials = async (dependency: string) => {
    const dependencyConfig = Referentials.referential.find(
      (r) => r.code === dependency
    ) as IReferentialConfig;
    const dependentReferentialList = await this.appReferentialService.GetFromDB(
      dependencyConfig.getUrl
    );
    return { dependentReferentialList, dependencyConfig };
  };

  loadReferentials = async (value: IReferentialConfig | null) => {
    let referentialList: IReferential[] = [];
    let dependentReferencials: IReferential[] = [];
    let dependentReferentialConfig: IReferentialConfig | null = null;
    if (value !== null && value.getUrl !== '') {
      try {
        referentialList = await this.appReferentialService.GetFromDB(value.getUrl);

        if (!R.isNil(value.dependency)) {
          const { dependentReferentialList, dependencyConfig } =
            await this.loadDependentReferentials(value.dependency);
          dependentReferencials = dependentReferentialList.filter((x) => x.isActive === true);
          if (value.code === ReferentialCode.FleetSupportSubsection) {
            dependentReferencials = dependentReferencials.filter((x) => R.isNil(x.dependencyId));
          }
          dependentReferentialConfig = dependencyConfig;

          this.setState({
            filteredReferentials: referentialList.filter(
              (ref) =>
                ref.dependencyId === this.state.selectedDependentReferential?.id ??
                dependentReferencials[0].id
            ),
            dependentReferencials: dependentReferencials,
            dependentReferentialConfig: dependentReferentialConfig,
            selectedDependentReferential:
              this.state.selectedDependentReferential ?? dependentReferencials[0]
          });
        }

        referentialList.forEach((refItem) => {
          this.state.languages.forEach((lang) => {
            const translation = refItem.translations.find((item) => item.language === lang.code);
            if (R.isNil(translation)) {
              refItem.translations.push({
                id: 0,
                name: '',
                language: lang.code,
                referentialTypeId: refItem.id
              } as ITranslation);
            }
          });
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }

    console.log(this.state);
    console.log(this.state.selectedDependentReferential ?? dependentReferencials[0]);
    console.log(
      referentialList.filter(
        (ref) =>
          ref.dependencyId === this.state.selectedDependentReferential?.id ??
          dependentReferencials[0].id
      )
    );
    this.setState({
      referentials: referentialList,
      isLoading: false,
      selectedReferential: value!,
      selectedDependentReferential:
        this.state.selectedDependentReferential ?? dependentReferencials[0],
      filteredReferentials:
        value?.code === ReferentialCode.FleetSupportSection
          ? referentialList.filter((ref) => R.isNil(ref.dependencyId))
          : referentialList.filter(
              (ref) =>
                ref.dependencyId === this.state.selectedDependentReferential?.id ??
                dependentReferencials[0].id
            )
    });
  };

  handleIsActiveChange = async (event: ChangeEvent<HTMLInputElement>, rowData: any) => {
    const id = rowData.id;
    const newReferentials = [...this.state.filteredReferentials];
    const updatedReferential = newReferentials.find((ref) => ref.id === id);
    if (updatedReferential !== undefined) {
      updatedReferential.isActive = event.target.checked;

      try {
        await this.appReferentialService.Update(
          updatedReferential,
          this.state.selectedReferential!.updateUrl
        );
        this.setState({
          referentials: newReferentials
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  handleIsNumberChange = async (event: ChangeEvent<HTMLInputElement>, rowData: any) => {
    const id = rowData.id;
    const newReferentials = [...this.state.filteredReferentials];
    const updatedReferential = newReferentials.find((ref) => ref.id === id);
    if (updatedReferential !== undefined) {
      updatedReferential.isNumber = event.target.checked;

      try {
        await this.appReferentialService.Update(
          updatedReferential,
          this.state.selectedReferential!.updateUrl
        );
        this.setState({
          referentials: newReferentials
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  handleIsFleetStatusChange = async (event: ChangeEvent<HTMLInputElement>, rowData: any) => {
    const id = rowData.id;
    const newReferentials = [...this.state.filteredReferentials];
    const updatedReferential = newReferentials.find((ref) => ref.id === id);
    if (updatedReferential !== undefined) {
      updatedReferential.isFleetStatus = event.target.checked;

      try {
        await this.appReferentialService.Update(
          updatedReferential,
          this.state.selectedReferential!.updateUrl
        );
        this.setState({
          referentials: newReferentials
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  handleIsFleetChange = async (event: ChangeEvent<HTMLInputElement>, rowData: any) => {
    const id = rowData.id;
    const newReferentials = [...this.state.filteredReferentials];
    const updatedReferential = newReferentials.find((ref) => ref.id === id);
    if (updatedReferential !== undefined) {
      updatedReferential.isFleet = event.target.checked;

      try {
        await this.appReferentialService.Update(
          updatedReferential,
          this.state.selectedReferential!.updateUrl
        );
        this.setState({
          referentials: newReferentials
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  handleIsAdminOnlyChange = async (event: ChangeEvent<HTMLInputElement>, rowData: any) => {
    const id = rowData.id;
    const newReferentials = [...this.state.filteredReferentials];
    const updatedReferential = newReferentials.find((ref) => ref.id === id);
    if (updatedReferential !== undefined) {
      updatedReferential.isAdminOnly = event.target.checked;

      try {
        await this.appReferentialService.Update(
          updatedReferential,
          this.state.selectedReferential!.updateUrl
        );
        this.setState({
          referentials: newReferentials
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  handleIsRCRAvailableChange = async (event: ChangeEvent<HTMLInputElement>, rowData: any) => {
    const id = rowData.id;
    const newReferentials = [...this.state.filteredReferentials];
    const updatedReferential = newReferentials.find((ref) => ref.id === id);
    if (updatedReferential !== undefined) {
      updatedReferential.isRCRAvailable = event.target.checked;

      try {
        await this.appReferentialService.Update(
          updatedReferential,
          this.state.selectedReferential!.updateUrl
        );
        this.setState({
          referentials: newReferentials
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  activeLanguages = () => {
    const activeLanguages =
      this.props.appState.appUser!.organization!.gtOrganization!.languages!.filter((language) => {
        if (language.isActive === true) {
          return language;
        }
      });
    activeLanguages.forEach((language) => {
      language.displayName =
        language.displayName.charAt(0).toUpperCase() + language.displayName.slice(1);
    });
    return activeLanguages;
  };

  handleLocListChange = (event: any, gridProps: any, code: string | null) => {
    let newLocList = [] as ITranslation[];
    if (gridProps.rowData.translations) {
      newLocList = gridProps.rowData.translations.map((loc: any) => {
        if (loc.language === code) {
          loc.name = event.target.value;
        }
        return loc;
      });
    }

    gridProps.onRowDataChange({
      ...gridProps.rowData,
      translations: newLocList
    });
  };

  handleLocDescriptionChange = (event: any, gridProps: any, code: string | null) => {
    const newLocList = gridProps.rowData.translations.map((loc: ITranslation) => {
      if (loc.language === code) {
        loc.description = event.target.value;
      }
      return loc;
    });

    gridProps.onRowDataChange({
      ...gridProps.rowData,
      translations: newLocList
    });
  };

  hasDescription = () => {
    return (
      !R.isNil(this.state.selectedReferential) &&
      [ReferentialCode.DamageType, ReferentialCode.DamageSeverity].includes(
        this.state.selectedReferential!.code
      )
    );
  };

  getDescriptionColumn = () => {
    return {
      field: 'description',
      title: this.translatorService.Tranlate('LIST_OF_VALUES_DESCRIPTION_HEADER', 'Descriere'),
      searchable: true,
      render: (rowData: any) => {
        return rowData.translations.map((val: ITranslation, index: number) => {
          const language = this.state.languages.find((language: any) => {
            return language.code === val.language;
          });
          return language ? (
            <div key={index} className="pt-2">
              <TextField
                label={language.displayName}
                fullWidth
                multiline
                value={val.description ?? ''}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true
                }}
                size="small"
              ></TextField>
            </div>
          ) : null;
        });
      },
      customFilterAndSearch: (value: string, rowData: any) => {
        return rowData.translations.some((val: ITranslation) =>
          (val.description ?? '').toLowerCase().includes(value.toLowerCase())
        );
      },
      editComponent: (props: any) => {
        if (R.isNil(props.rowData.translations)) {
          return null;
        }

        return props.rowData.translations.map((val: ITranslation, index: number) => {
          const language = this.state.languages.find(
            (language: any) => language.code === val.language
          );

          return language ? (
            <div key={index} className="pt-2">
              <TextField
                size="small"
                name={'description_' + index}
                fullWidth
                multiline
                value={val.description ?? ''}
                label={language.displayName}
                onChange={(e) => this.handleLocDescriptionChange(e, props, val.language)}
              ></TextField>
            </div>
          ) : null;
        });
      }
    } as Column<any>;
  };

  isDamageSeverity = () => {
    return this.state.selectedReferential?.code === ReferentialCode.DamageSeverity;
  };

  getDamageSeverityColumns = () => {
    return [
      {
        field: 'level',
        title: this.translatorService.Tranlate('LIST_OF_VALUES_LEVEL_HEADER', 'Nivel'),
        type: 'numeric',
        editComponent: (props: EditComponentProps<any>) => {
          return (
            <TextField
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={props.value ?? ''}
              onChange={(e) =>
                props.onChange(e.target.value === '' ? null : Number(e.target.value))
              }
            />
          );
        }
      },
      {
        field: 'damageLevelValue',
        title: this.translatorService.Tranlate(
          'LIST_OF_VALUES_DAMAGE_LEVEL_VALUE_HEADER',
          'Grad avarie GT'
        ),
        initialEditValue: 2,
        render: (rowData: IReferential) =>
          this.getDamageLevelValueLabel(rowData.damageLevelValue ?? 2),
        editComponent: (props: EditComponentProps<any>) => {
          return (
            <TextField
              select
              size="small"
              value={props.value ?? 2}
              onChange={(e) => props.onChange(Number(e.target.value))}
            >
              {[1, 2, 3, 4].map((value) => (
                <MenuItem key={value} value={value}>
                  {this.getDamageLevelValueLabel(value)}
                </MenuItem>
              ))}
            </TextField>
          );
        }
      }
    ] as Column<any>[];
  };

  getDamageLevelValueLabel = (value: number) => {
    const labels: Record<number, string> = {
      1: this.translatorService.Tranlate('LIST_OF_VALUES_DAMAGE_LEVEL_VALUE_1', 'Inlocuire'),
      2: this.translatorService.Tranlate(
        'LIST_OF_VALUES_DAMAGE_LEVEL_VALUE_2',
        'Vopsire de suprafata'
      ),
      3: this.translatorService.Tranlate('LIST_OF_VALUES_DAMAGE_LEVEL_VALUE_3', 'Reparatie < 50%'),
      4: this.translatorService.Tranlate('LIST_OF_VALUES_DAMAGE_LEVEL_VALUE_4', 'Reparatie > 50%')
    };
    return R.isNil(labels[value]) ? '' : `${value} - ${labels[value]}`;
  };

  getColumns = () => {
    if (this.state.featureReferential) {
      return [
        {
          field: 'code',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_COD_HEADER', 'Cod'),
          searchable: true
          // initialEditValue: Guid.create().toString()
        } as Column<any>,
        {
          field: 'translations',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_NAME_HEADER', 'Nume'),
          searchable: true,
          render: (rowData: any) => {
            return rowData.translations.map((val: ITranslation, index: number) => {
              const language = this.state.languages.find((language: any) => {
                return language.code === val.language;
              });
              return language ? (
                <div key={index} className="pt-2">
                  <TextField
                    id="standard-basic"
                    label={language.displayName}
                    fullWidth
                    value={val.name}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true
                    }}
                    size="small"
                  ></TextField>{' '}
                </div>
              ) : null;
            });
          },
          customFilterAndSearch: (value, rowData) => {
            let matchFilter = false;
            rowData.translations.map((val: any) => {
              const findMatch = val.name.toLowerCase().indexOf(value.toLowerCase()) != -1;
              if (findMatch) {
                matchFilter = true;
              }
            });
            return matchFilter;
          },
          editComponent: (props: any) => {
            if (!R.isNil(props.value)) {
              return props.value.map((val: ITranslation, index: number) => {
                const language = this.state.languages.find(
                  (language: any) => language.code === val.language
                );

                return language ? (
                  <div key={index} className="pt-2">
                    <TextField
                      id="standard-basic"
                      size="small"
                      name={'name_' + index}
                      fullWidth
                      value={val.name}
                      label={language.displayName}
                      onChange={(e) => this.handleLocListChange(e, props, val.language)}
                    ></TextField>
                  </div>
                ) : null;
              });
            } else {
              //add
              const newLocList = [] as ITranslation[];
              for (let i = 0; i < this.state.languages!.length; i++) {
                const locListItem = {} as ITranslation;
                locListItem.language = this.state.languages[i].code;
                locListItem.name = '';
                newLocList.push(locListItem);
              }

              props.onChange(newLocList);

              return null;
            }
          }
        } as Column<any>,
        {
          field: 'dateModification',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_DATE_HEADER', 'Data'),
          defaultSort: 'desc',
          searchable: true,
          render: (rowData: any) => {
            return moment
              .utc(rowData.dateModification)
              .local()
              .format(this.props.appState.longDateFormat);
          },
          editComponent: (props: EditComponentProps<any>) => {
            return <Label>{moment(new Date()).format(this.props.appState.longDateFormat)}</Label>;
          }
        } as Column<any>,
        {
          field: 'isNumber',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_IS_NUMBER_HEADER', 'Tip numar'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isNumber}
                onChange={(e) => this.handleIsNumberChange(e, rowData)}
                value={rowData.isNumber}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'isActive',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_ISACTIVE_HEADER', 'Stare'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isActive}
                onChange={(e) => this.handleIsActiveChange(e, rowData)}
                value={rowData.isActive}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        }
      ];
    }

    if (this.state.vehicleStatusReferential || this.state.caseStatusReferential) {
      return [
        {
          field: 'code',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_COD_HEADER', 'Cod'),
          searchable: true
          // initialEditValue: Guid.create().toString()
        } as Column<any>,
        {
          field: 'translations',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_NAME_HEADER', 'Nume'),
          searchable: true,
          render: (rowData: any) => {
            return rowData.translations.map((val: ITranslation, index: number) => {
              const language = this.state.languages.find((language: any) => {
                return language.code === val.language;
              });
              return language ? (
                <div key={index} className="pt-2">
                  <TextField
                    id="standard-basic"
                    label={language.displayName}
                    fullWidth
                    value={val.name}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true
                    }}
                    size="small"
                  ></TextField>{' '}
                </div>
              ) : null;
            });
          },
          customFilterAndSearch: (value, rowData) => {
            let matchFilter = false;
            rowData.translations.map((val: any) => {
              const findMatch = val.name.toLowerCase().indexOf(value.toLowerCase()) != -1;
              if (findMatch) {
                matchFilter = true;
              }
            });
            return matchFilter;
          },
          editComponent: (props: any) => {
            if (!R.isNil(props.value)) {
              return props.value.map((val: ITranslation, index: number) => {
                const language = this.state.languages.find(
                  (language: any) => language.code === val.language
                );

                return language ? (
                  <div key={index} className="pt-2">
                    <TextField
                      id="standard-basic"
                      size="small"
                      name={'name_' + index}
                      fullWidth
                      value={val.name}
                      label={language.displayName}
                      onChange={(e) => this.handleLocListChange(e, props, val.language)}
                    ></TextField>
                  </div>
                ) : null;
              });
            } else {
              //add
              const newLocList = [] as ITranslation[];
              for (let i = 0; i < this.state.languages!.length; i++) {
                const locListItem = {} as ITranslation;
                locListItem.language = this.state.languages[i].code;
                locListItem.name = '';
                newLocList.push(locListItem);
              }

              props.onChange(newLocList);

              return null;
            }
          }
        } as Column<any>,
        {
          field: 'dateModification',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_DATE_HEADER', 'Data'),
          defaultSort: 'desc',
          searchable: true,
          render: (rowData: any) => {
            return moment
              .utc(rowData.dateModification)
              .local()
              .format(this.props.appState.longDateFormat);
          },
          editComponent: (props: EditComponentProps<any>) => {
            return <Label>{moment(new Date()).format(this.props.appState.longDateFormat)}</Label>;
          }
        } as Column<any>,
        {
          field: 'isFleetStatus',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_ISFLEET_STATUS_HEADER',
            'Status Fleet'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isFleetStatus}
                onChange={(e) => this.handleIsFleetStatusChange(e, rowData)}
                value={rowData.isFleetStatus}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'isActive',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_ISACTIVE_HEADER', 'Stare'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isActive}
                onChange={(e) => this.handleIsActiveChange(e, rowData)}
                value={rowData.isActive}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        }
      ];
    }
    if (this.state.fuelTypeReferential) {
      const columns: Column<any>[] = [
        {
          field: 'code',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_COD_HEADER', 'Cod'),
          searchable: true
          // initialEditValue: Guid.create().toString()
        } as Column<any>,
        {
          field: 'translations',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_NAME_HEADER', 'Nume'),
          searchable: true,
          render: (rowData: any) => {
            return rowData.translations.map((val: ITranslation, index: number) => {
              const language = this.state.languages.find((language: any) => {
                return language.code === val.language;
              });
              return language ? (
                <div key={index} className="pt-2">
                  <TextField
                    id="standard-basic"
                    label={language.displayName}
                    fullWidth
                    value={val.name}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true
                    }}
                    size="small"
                  ></TextField>{' '}
                </div>
              ) : null;
            });
          },
          customFilterAndSearch: (value, rowData) => {
            let matchFilter = false;
            rowData.translations.map((val: any) => {
              const findMatch = val.name.toLowerCase().indexOf(value.toLowerCase()) != -1;
              if (findMatch) {
                matchFilter = true;
              }
            });
            return matchFilter;
          },
          editComponent: (props: any) => {
            if (!R.isNil(props.value)) {
              return props.value.map((val: ITranslation, index: number) => {
                const language = this.state.languages.find(
                  (language: any) => language.code === val.language
                );

                return language ? (
                  <div key={index} className="pt-2">
                    <TextField
                      id="standard-basic"
                      size="small"
                      name={'name_' + index}
                      fullWidth
                      value={val.name}
                      label={language.displayName}
                      onChange={(e) => this.handleLocListChange(e, props, val.language)}
                    ></TextField>
                  </div>
                ) : null;
              });
            } else {
              //add
              const newLocList = [] as ITranslation[];
              for (let i = 0; i < this.state.languages!.length; i++) {
                const locListItem = {} as ITranslation;
                locListItem.language = this.state.languages[i].code;
                locListItem.name = '';
                newLocList.push(locListItem);
              }

              props.onChange(newLocList);

              return null;
            }
          }
        } as Column<any>,
        {
          field: 'dateModification',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_DATE_HEADER', 'Data'),
          defaultSort: 'desc',
          searchable: true,
          render: (rowData: any) => {
            return moment
              .utc(rowData.dateModification)
              .local()
              .format(this.props.appState.longDateFormat);
          },
          editComponent: (props: EditComponentProps<any>) => {
            return <Label>{moment(new Date()).format(this.props.appState.longDateFormat)}</Label>;
          }
        } as Column<any>,
        {
          field: 'isRCRAvailable',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_RCR_AVAILABLE_HEADER',
            'Disponibil RCR'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isRCRAvailable}
                onChange={(e) => this.handleIsRCRAvailableChange(e, rowData)}
                value={rowData.isRCRAvailable}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'isActive',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_ISACTIVE_HEADER', 'Stare'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isActive}
                onChange={(e) => this.handleIsActiveChange(e, rowData)}
                value={rowData.isActive}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        }
      ];

      // Add isFleet column only for FuelType referential
      if (this.state.selectedReferential?.code === ReferentialCode.FuelType) {
        columns.push({
          field: 'isFleet',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_IS_FLEET_HEADER', 'Fleet'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isFleet}
                onChange={(e) => this.handleIsFleetChange(e, rowData)}
                value={rowData.isFleet}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        });
      }

      return columns;
    } else if (this.state.organizationTypeReferential) {
      return [
        {
          field: 'code',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_COD_HEADER', 'Cod'),
          searchable: true
          // initialEditValue: Guid.create().toString()
        } as Column<any>,
        {
          field: 'translations',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_NAME_HEADER', 'Nume'),
          searchable: true,
          render: (rowData: any) => {
            return rowData.translations.map((val: ITranslation, index: number) => {
              const language = this.state.languages.find((language: any) => {
                return language.code === val.language;
              });
              return language ? (
                <div key={index} className="pt-2">
                  <TextField
                    id="standard-basic"
                    label={language.displayName}
                    fullWidth
                    value={val.name}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true
                    }}
                    size="small"
                  ></TextField>{' '}
                </div>
              ) : null;
            });
          },
          customFilterAndSearch: (value, rowData) => {
            let matchFilter = false;
            rowData.translations.map((val: any) => {
              const findMatch = val.name.toLowerCase().indexOf(value.toLowerCase()) != -1;
              if (findMatch) {
                matchFilter = true;
              }
            });
            return matchFilter;
          },
          editComponent: (props: any) => {
            if (!R.isNil(props.value)) {
              return props.value.map((val: ITranslation, index: number) => {
                const language = this.state.languages.find(
                  (language: any) => language.code === val.language
                );

                return language ? (
                  <div key={index} className="pt-2">
                    <TextField
                      id="standard-basic"
                      size="small"
                      name={'name_' + index}
                      fullWidth
                      value={val.name}
                      label={language.displayName}
                      onChange={(e) => this.handleLocListChange(e, props, val.language)}
                    ></TextField>
                  </div>
                ) : null;
              });
            } else {
              //add
              const newLocList = [] as ITranslation[];
              for (let i = 0; i < this.state.languages!.length; i++) {
                const locListItem = {} as ITranslation;
                locListItem.language = this.state.languages[i].code;
                locListItem.name = '';
                newLocList.push(locListItem);
              }

              props.onChange(newLocList);

              return null;
            }
          }
        } as Column<any>,
        {
          field: 'dateModification',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_DATE_HEADER', 'Data'),
          defaultSort: 'desc',
          searchable: true,
          render: (rowData: any) => {
            return moment
              .utc(rowData.dateModification)
              .local()
              .format(this.props.appState.longDateFormat);
          },
          editComponent: (props: EditComponentProps<any>) => {
            return <Label>{moment(new Date()).format(this.props.appState.longDateFormat)}</Label>;
          }
        } as Column<any>,
        {
          field: 'takeOrganizationTariff',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_TAKE_ORGANIZATION_TARIFF_HEADER',
            'Tarif Organizatie'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.takeOrganizationTariff}
                value={rowData.takeOrganizationTariff}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'hideAlternative',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_HIDE_ALTERNATIVE_HEADER',
            'Ascunde Alternative'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.hideAlternative}
                value={rowData.hideAlternative}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'hideInsurerCalculation',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_HIDE_INSURER_TARIFF_HEADER',
            'Ascunde calculatie OE cu tarife asigurator'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.hideInsurerCalculation}
                value={rowData.hideInsurerCalculation}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'hideOrderPrices',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_HIDE_ORDER_PRICES_HEADER',
            'Ascunde Preturi Comanda'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.hideOrderPrices}
                value={rowData.hideOrderPrices}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'hideOtherAttachments',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_HIDE_OTHER_ATTACHMENTS_HEADER',
            'Ascunde Altele'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.hideOtherAttachments}
                value={rowData.hideOtherAttachments}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'hideGtmOffer',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_HIDE_GTM_OFFER_HEADER',
            'Ascunde oferta generata de GTM'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.hideGtmOffer}
                value={rowData.hideGtmOffer}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'viewHiddenServiceDocuments',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_VIEW_HIDDEN_SERVICE_DOCUMENTS_HEADER',
            'Vezi documente ascunse pentru service'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.viewHiddenServiceDocuments}
                value={rowData.viewHiddenServiceDocuments}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        },
        {
          field: 'isActive',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_ISACTIVE_HEADER', 'Stare'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isActive}
                onChange={(e) => this.handleIsActiveChange(e, rowData)}
                value={rowData.isActive}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        }
      ];
    } else {
      const columns: Column<any>[] = [
        {
          field: 'code',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_COD_HEADER', 'Cod'),
          searchable: true
          // initialEditValue: Guid.create().toString()
        } as Column<any>,
        {
          field: 'translations',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_NAME_HEADER', 'Nume'),
          searchable: true,
          render: (rowData: any) => {
            return rowData.translations.map((val: ITranslation, index: number) => {
              const language = this.state.languages.find((language: any) => {
                return language.code === val.language;
              });
              return language ? (
                <div key={index} className="pt-2">
                  <TextField
                    id="standard-basic"
                    label={language.displayName}
                    fullWidth
                    value={val.name}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true
                    }}
                    size="small"
                  ></TextField>{' '}
                </div>
              ) : null;
            });
          },
          customFilterAndSearch: (value, rowData) => {
            let matchFilter = false;
            rowData.translations.map((val: any) => {
              const findMatch = val.name.toLowerCase().indexOf(value.toLowerCase()) != -1;
              if (findMatch) {
                matchFilter = true;
              }
            });
            return matchFilter;
          },
          editComponent: (props: any) => {
            if (!R.isNil(props.value)) {
              return props.value.map((val: ITranslation, index: number) => {
                const language = this.state.languages.find(
                  (language: any) => language.code === val.language
                );

                return language ? (
                  <div key={index} className="pt-2">
                    <TextField
                      id="standard-basic"
                      size="small"
                      name={'name_' + index}
                      fullWidth
                      value={val.name}
                      label={language.displayName}
                      onChange={(e) => this.handleLocListChange(e, props, val.language)}
                    ></TextField>
                  </div>
                ) : null;
              });
            } else {
              //add
              const newLocList = [] as ITranslation[];
              for (let i = 0; i < this.state.languages!.length; i++) {
                const locListItem = {} as ITranslation;
                locListItem.language = this.state.languages[i].code;
                locListItem.name = '';
                newLocList.push(locListItem);
              }

              props.onChange(newLocList);

              return null;
            }
          }
        } as Column<any>,
        {
          field: 'dateModification',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_DATE_HEADER', 'Data'),
          defaultSort: 'desc',
          searchable: true,
          render: (rowData: any) => {
            return moment
              .utc(rowData.dateModification)
              .local()
              .format(this.props.appState.longDateFormat);
          },
          editComponent: (props: EditComponentProps<any>) => {
            return <Label>{moment(new Date()).format(this.props.appState.longDateFormat)}</Label>;
          }
        } as Column<any>,
        {
          field: 'isActive',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_ISACTIVE_HEADER', 'Stare'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isActive}
                onChange={(e) => this.handleIsActiveChange(e, rowData)}
                value={rowData.isActive}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        }
      ];

      if (this.state.selectedReferential?.code === ReferentialCode.CostCenter) {
        columns.push({
          field: 'managementCode',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_COD_GESTIUNE_HEADER',
            'Cod Gestiune'
          ),
          searchable: true
        } as Column<any>);
      }

      // Add isFleet column conditionally for specific referential codes
      const shouldShowIsFleet =
        this.state.selectedReferential &&
        [
          ReferentialCode.VehicleMake,
          ReferentialCode.Model,
          ReferentialCode.BodyType,
          ReferentialCode.TractionType,
          ReferentialCode.TransmissionType,
          ReferentialCode.Color,
          ReferentialCode.FleetVehicleType
        ].includes(this.state.selectedReferential.code);

      if (shouldShowIsFleet) {
        columns.push({
          field: 'isFleet',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_IS_FLEET_HEADER', 'Fleet'),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isFleet}
                onChange={(e) => this.handleIsFleetChange(e, rowData)}
                value={rowData.isFleet}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        });
      }

      const shouldShowIsAdminOnly =
        this.state.selectedReferential &&
        [ReferentialCode.FleetSupportSection, ReferentialCode.FleetSupportSubsection].includes(
          this.state.selectedReferential.code
        );

      if (this.hasDescription()) {
        columns.splice(2, 0, this.getDescriptionColumn());
      }

      if (this.isDamageSeverity()) {
        columns.splice(3, 0, ...this.getDamageSeverityColumns());
      }

      if (shouldShowIsAdminOnly) {
        columns.push({
          field: 'isAdminOnly',
          title: this.translatorService.Tranlate(
            'LIST_OF_VALUES_IS_ADMIN_ONLY_HEADER',
            'Doar admin'
          ),
          render: (rowData: any) => {
            return (
              <Switch
                checked={rowData.isAdminOnly}
                onChange={(e) => this.handleIsAdminOnlyChange(e, rowData)}
                value={rowData.isAdminOnly}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          },

          editComponent: (props: EditComponentProps<any>) => {
            return (
              <Switch
                checked={props.value || false}
                value={props.value || false}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={(e) => props.onChange(e.target.checked)}
              />
            );
          }
        });
      }

      if (this.state.selectedReferential?.code === ReferentialCode.GTPart) {
        columns.push({
          field: 'cupiCodes',
          title: this.translatorService.Tranlate('LIST_OF_VALUES_CUPI_CODES_HEADER', 'Coduri CUPI'),
          searchable: true
        } as Column<any>);
      }

      return columns;
    }
  };
  public getLocalization = () => {
    return {
      header: { actions: '' },
      toolbar: {
        searchPlaceholder: this.translatorService.Tranlate('SEARCH', 'Cauta'),
        searchTooltip: this.translatorService.Tranlate('SEARCH', 'Cauta')
      },
      body: {
        editTooltip: this.translatorService.Tranlate('EDIT', 'Editeaza'),
        addTooltip: this.translatorService.Tranlate('Add', 'Adauga'),
        emptyDataSourceMessage: this.translatorService.Tranlate(
          'NO_RECORDS_TO_DISPLAY',
          'Nu exista date de afisat'
        ),
        editRow: {
          cancelTooltip: this.translatorService.Tranlate('CANCEL', 'Anuleaza'),
          saveTooltip: this.translatorService.Tranlate('SAVE', 'Salveaza')
        }
      },
      pagination: {
        labelRowsSelect: this.translatorService.Tranlate('ROWS', 'inregistrari'),
        firstTooltip: this.translatorService.Tranlate('FIRST_PAGE', 'Prima pagina'),
        previousTooltip: this.translatorService.Tranlate('PREVIOUS_PAGE', 'Pagina precedenta'),
        nextTooltip: this.translatorService.Tranlate('NEXT_PAGE', 'Pagina urmatoare'),
        lastTooltip: this.translatorService.Tranlate('LAST_PAGE', 'Ultima pagina'),
        labelDisplayedRows:
          '{from}-{to} ' + this.translatorService.Tranlate('OF', 'din') + ' {count}'
      }
    };
  };
  public renderTypeList = () => {
    const ListsOFValueTypes = Referentials.referential;

    return (
      <Fragment>
        <Card className="card-box p-4 mb-4">
          <div className="d-flex justify-content-between w-100">
            <Autocomplete
              id="combo-box-demo"
              options={ListsOFValueTypes}
              getOptionLabel={(option) => option.name}
              style={{ width: '100%' }}
              onChange={this.onTagsChange}
              renderInput={(params: RenderInputParams) => (
                <TextField
                  fullWidth
                  {...params}
                  label={this.translatorService.Tranlate(
                    'LIST_OF_VALUES_TYPE',
                    'Tip lista de valori'
                  )}
                />
              )}
            />
          </div>
        </Card>

        {this.state.dependentReferentialConfig && (
          <Card className="card-box p-4 mb-4">
            <Autocomplete
              id="dependencies"
              options={this.state.dependentReferencials}
              disableClearable
              value={this.state.selectedDependentReferential}
              onChange={(e: any, newValue: IReferential | null) =>
                this.handleDependentReferencial(newValue)
              }
              getOptionLabel={(option) => option.displayName || ''}
              renderInput={(params: RenderInputParams) => (
                <TextField
                  fullWidth
                  {...params}
                  // inputProps={{ ...params.inputProps, readOnly: true }}
                  label={this.translatorService.Tranlate(
                    'LIST_OF_DEPENDENT_VALUES',
                    'Tip lista de valori'
                  )}
                />
              )}
            />
          </Card>
        )}
      </Fragment>
    );
  };

  onRowUpdate = (newData: IReferential, oldData?: IReferential): Promise<void> =>
    new Promise(async (resolve, reject) => {
      if (!this.validateRef(newData)) {
        reject();
        return;
      }

      if (oldData) {
        this.setState((prevState: IReferentialState) => {
          const newReferentials = !R.isEmpty(this.state.filteredReferentials)
            ? [...this.state.filteredReferentials]
            : [...this.state.referentials];
          newReferentials[newReferentials.indexOf(oldData)] = newData;

          return { ...prevState, referentials: newReferentials };
        });

        try {
          await this.appReferentialService.Update(
            newData,
            this.state.selectedReferential!.updateUrl
          );
          await this.loadReferentials(this.state.selectedReferential);
          resolve();
          this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
            variant: 'success'
          });
        } catch (error) {
          this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
            variant: 'error'
          });
          reject();
        }
      }
    });

  validateRef = (referential: IReferential): boolean => {
    if (referential.code === '' || referential.code === undefined || referential.code === null) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('LIST_OF_VALUES_ERROR_CODE_EMPTY', 'Eroare'),
        { variant: 'error' }
      );
      return false;
    }
    const referentials =
      !R.isEmpty(this.state.filteredReferentials) && !this.isDamageSeverity()
        ? this.state.filteredReferentials
        : this.state.referentials;
    let resultRef = referentials.find((item) => item.code === referential.code);
    if (referential.id !== undefined) {
      resultRef = referentials.find(
        (item) => item.code === referential.code && item.id !== referential.id
      );
    }
    if (resultRef !== undefined) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('LIST_OF_VALUES_ERROR_CODE_UNIQUE', 'Eroare'),
        { variant: 'error' }
      );
      return false;
    }
    if (referential.translations === undefined) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('LIST_OF_VALUES_ERROR_NAME', 'Eroare'),
        { variant: 'error' }
      );
      return false;
    }
    for (const item of referential.translations) {
      if (item.name === '' || item.name === undefined || item.name === null) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate('LIST_OF_VALUES_ERROR_NAME', 'Eroare'),
          { variant: 'error' }
        );
        return false;
      }
    }
    if (
      this.isDamageSeverity() &&
      (R.isNil(referential.level) || !Number.isInteger(referential.level) || referential.level < 1)
    ) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('LIST_OF_VALUES_ERROR_LEVEL', 'Eroare'),
        { variant: 'error' }
      );
      return false;
    }
    if (
      this.hasDescription() &&
      referential.translations.some((item) => R.isEmpty(item.description ?? ''))
    ) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('LIST_OF_VALUES_ERROR_DESCRIPTION', 'Eroare'),
        { variant: 'error' }
      );
      return false;
    }

    return true;
  };

  onRowAdd = (newData: IReferential): Promise<void> =>
    new Promise(async (resolve, reject) => {
      if (!this.validateRef(newData)) {
        reject();
        return;
      }
      try {
        newData.name = newData.translations[0].name;
        if (this.hasDescription()) {
          newData.description = newData.translations[0].description ?? '';
        }
        newData.gtId = this.props.appState.appUser!.gtId!;
        if (!R.isNil(this.state.selectedDependentReferential)) {
          newData.dependencyId = this.state.selectedDependentReferential.id;
        }
        await this.appReferentialService.Add(newData, this.state.selectedReferential!.addUrl);
        await this.loadReferentials(this.state.selectedReferential);
        resolve();
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
        reject();
      }
    });

  public renderTable = () => {
    if (this.state.selectedReferential === null) {
      return;
    }

    return (
      <MaterialTable
        icons={tableIcons}
        title={this.translatorService.Tranlate('LIST_OF_VALUES', 'Lista de valori')}
        columns={this.getColumns()}
        data={this.state.filteredReferentials}
        options={{
          actionsColumnIndex: -1,
          addRowPosition: 'first'
        }}
        localization={this.getLocalization()}
        editable={{
          onRowAdd: (newData: IReferential) => this.onRowAdd(newData),
          onRowUpdate: (newData: any, oldData?: any) => this.onRowUpdate(newData, oldData)
        }}
      />
    );
  };
  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.appReferentialService = (this.context as AppContext).referentialService;

    return (
      <Box m={1} p={1}>
        {this.renderTypeList()}
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
        </div>
        {!this.state.isLoading &&
          (this.state.selectedReferential?.code === ReferentialCode.City ? (
            <CityAppReferentials />
          ) : this.state.selectedReferential?.code === ReferentialCode.LeaseProvider ? (
            <LeaseProviderAppReferentials />
          ) : this.state.selectedReferential?.code === ReferentialCode.ListValues ? (
            <ListValuesAppReferentials />
          ) : this.state.selectedReferential?.code === ReferentialCode.TiresDiameter ? (
            <TiresDiameterAppReferentials />
          ) : (
            this.renderTable()
          ))}
      </Box>
    );
  }
}

export default connect((state: ApplicationState) => ({ appState: state.app }))(
  withSnackbar(AppReferentials as any)
);
